import * as GC from '@mescius/spread-sheets';

import type { PropertyTag, CollectedProperty, CellTag } from '../types';
import {
  getCellTagSafely,
  hasPropertyTag,
  removePropertyFromCellTag,
  updateCellTagSafely,
} from './cellTag';
import { isCellInAnyLoop } from './loop';
import { getCellBoundsWithSpan, toRangeAddress } from './range';
import { applyCellBackground, clearCellBackground } from './style';

import { ACTIVE_CELL_COLOR, ACTIVE_CELL_COLOR_IN_LOOP } from '../constants';

/**
 * セルにプロパティを設定する
 * 仕様:
 * - タグの追加
 * - プロパティ名の表示
 * - 背景色の変更
 * @param sheet
 * @param row
 * @param col
 * @param property
 */
export function setPropertyByCell(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  property: PropertyTag
): void {
  updateCellTagSafely(sheet, row, col, (prev) => ({
    ...prev,
    property,
  }));

  sheet.getCell(row, col).value(property.propertyName);

  const containingLoop = isCellInAnyLoop(sheet, row, col);

  applyCellBackground(
    sheet,
    row,
    col,
    containingLoop ? ACTIVE_CELL_COLOR_IN_LOOP : ACTIVE_CELL_COLOR
  );
}

/**
 * セルのプロパティを削除する
 * 仕様:
 * - タグの削除
 * - プロパティ名の削除
 * - 背景色の削除
 * @param sheet
 * @param row
 * @param col
 * @param cellBackgroundColor
 */
export function removePropertyByCell(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  cellBackgroundColor:
    | string
    | GC.Spread.Sheets.IPatternFill
    | undefined = undefined
): void {
  const tag = getCellTagSafely(sheet, row, col);

  if (!hasPropertyTag(tag)) {
    return;
  }

  updateCellTagSafely(sheet, row, col, (prev) =>
    removePropertyFromCellTag(prev)
  );

  sheet.getCell(row, col).clear(GC.Spread.Sheets.StorageType.data);
  if (cellBackgroundColor) {
    applyCellBackground(sheet, row, col, cellBackgroundColor);
    return;
  }

  clearCellBackground(sheet, row, col);
}

/**
 * シート上のプロパティの設定されたセルを集める
 * @param sheet
 * @returns
 */
export function collectProperties(
  sheet: GC.Spread.Sheets.Worksheet
): CollectedProperty[] {
  const usedRange = sheet.getUsedRange(GC.Spread.Sheets.UsedRangeType.tag);
  if (!usedRange) return [];

  const result: CollectedProperty[] = [];
  const visited = new Set<string>();

  const rowEnd = usedRange.row + usedRange.rowCount;
  const colEnd = usedRange.col + usedRange.colCount;

  for (let row = usedRange.row; row < rowEnd; row++) {
    for (let col = usedRange.col; col < colEnd; col++) {
      const tag = sheet.getTag(row, col) as CellTag | undefined;
      if (!tag?.property) continue;

      const bounds = getCellBoundsWithSpan(sheet, row, col);
      const key = `${bounds.startRow}:${bounds.startCol}`;

      if (visited.has(key)) {
        continue;
      }

      visited.add(key);

      result.push({
        ...tag.property,
        rowIndex: bounds.startRow,
        colIndex: bounds.startCol,
        targetCell: toRangeAddress(
          bounds.startRow,
          bounds.startCol,
          bounds.endRow,
          bounds.endCol
        ),
      });
    }
  }

  return result;
}
